import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Package, ArrowRight, Shield, Truck, Clock, Search, ChevronDown, User, UserCog, Lock, Users, Smartphone, Car, MapPin, BellRing, KeyRound, X } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { Footer } from '../components/Footer';
import { HeroCarousel } from '../components/HeroCarousel';
import { InstallAppButton } from '../components/InstallAppButton';
import { OtpResendButton } from '../components/OtpResendButton';
import axios from 'axios';
import { isMsg91Enabled, loadMsg91Widget, msg91SendOtp, msg91VerifyOtp, msg91RetryOtp } from '../lib/msg91';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HTML5-spec-compliant email regex — mirrors backend `_EMAIL_RE`.
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const isValidEmail = (v) => {
  const s = (v || '').trim();
  return s.length > 0 && s.length <= 254 && EMAIL_RE.test(s);
};

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Persisted OTP session — restored if the mobile browser kills the page
  // while the user switches to the SMS app. Cleared on success or cancel.
  const OTP_SESSION_KEY = 'lfj_otp_session_v1';
  const loadOtpSession = () => {
    try {
      const raw = sessionStorage.getItem(OTP_SESSION_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw);
      // 15 min freshness (matches MSG91 OTP expiry)
      if (Date.now() - (s.ts || 0) > 15 * 60 * 1000) {
        sessionStorage.removeItem(OTP_SESSION_KEY);
        return null;
      }
      return s;
    } catch (e) {
      return null;
    }
  };
  const saveOtpSession = (s) => {
    try {
      sessionStorage.setItem(OTP_SESSION_KEY, JSON.stringify({ ...s, ts: Date.now() }));
    } catch (e) { console.warn('[landing] OTP session write blocked (quota or private mode):', e); }
  };
  const clearOtpSession = () => {
    try { sessionStorage.removeItem(OTP_SESSION_KEY); } catch (e) { console.warn('[landing] OTP session clear blocked:', e); }
  };

  // Cities served (loaded from backend pickup-locations endpoint)
  const [citiesServed, setCitiesServed] = useState([]);
  useEffect(() => {
    axios.get(`${API}/pickup-locations`)
      .then(res => setCitiesServed(res.data?.locations || []))
      .catch(() => { /* silent — section just won't render */ });
  }, []);

  // Sign-in dropdown (top-right)
  const [signInOpen, setSignInOpen] = useState(false);

  // Existing user (Mobile + OTP) modal
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [existingStep, setExistingStep] = useState('mobile'); // 'mobile' | 'otp'
  const [existingMobile, setExistingMobile] = useState('');
  const [existingOtp, setExistingOtp] = useState('');
  const [existingError, setExistingError] = useState('');
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingHint, setExistingHint] = useState(false);

  // Registration (Mobile + OTP + details) modal — for new users only
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [regStep, setRegStep] = useState('mobile'); // 'mobile' | 'otp' | 'details'
  const [regMobile, setRegMobile] = useState('');
  const [regOtp, setRegOtp] = useState('');
  const [regHint, setRegHint] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [residentialAddress, setResidentialAddress] = useState('');
  const [notificationConsent, setNotificationConsent] = useState(false);
  const [pendingUserId, setPendingUserId] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  // Where to navigate after successful auth: 'book' → /luggage-form, else /dashboard
  const [postAuthIntent, setPostAuthIntent] = useState('dashboard');
  const postAuthDestination = () => (postAuthIntent === 'book' ? '/luggage-form' : '/dashboard');

  // "Book a pickup" → ALWAYS gate with mobile + OTP (no fast-path even for
  // a cached/logged-in user_id). New users auto-transition to registration;
  // returning users sign in and proceed. This guarantees a fresh OTP-verified
  // session on every booking attempt.
  const handleBookPickup = useCallback(() => {
    setPostAuthIntent('book');
    setShowRegisterModal(true);
  }, []);

  // External CTAs (Pricing/FAQ "Book a pickup") link here with ?book=1.
  // Auto-trigger the same auth-gated booking flow on arrival.
  // FAQ/Pricing "Sign In" also lands here with ?signin=1 → open the
  // existing-customer modal directly (partner/agent/admin logins are
  // no longer publicly linked from customer-facing pages).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('book') === '1') {
      handleBookPickup();
      window.history.replaceState({}, '', location.pathname);
    } else if (params.get('signin') === '1') {
      setShowExistingModal(true);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search, location.pathname, handleBookPickup]);

  // On mount: if we have a saved OTP session (mobile entered, awaiting OTP),
  // re-open the right modal at the OTP step. This rescues the user after a
  // mobile-browser background-tab kill while they were checking SMS.
  useEffect(() => {
    const s = loadOtpSession();
    if (!s) return;
    if (s.flow === 'existing') {
      setExistingMobile(s.mobile || '');
      setExistingStep('otp');
      setShowExistingModal(true);
    } else if (s.flow === 'register') {
      setRegMobile(s.mobile || '');
      setRegStep('otp');
      setShowRegisterModal(true);
    }
    // We intentionally do NOT re-trigger sendOtp — the SMS already went out
    // and the user is coming back to enter it. They can hit "Resend" if needed.
  }, []);

  // Preload MSG91 widget script as soon as either OTP modal opens.
  useEffect(() => {
    if ((showExistingModal || showRegisterModal) && isMsg91Enabled()) {
      loadMsg91Widget().catch(err => console.warn('MSG91 widget load failed', err));
    }
  }, [showExistingModal, showRegisterModal]);

  // ============== EXISTING USER FLOW (Mobile + OTP) ==============
  const resetExisting = () => {
    setShowExistingModal(false);
    setExistingStep('mobile');
    setExistingMobile('');
    setExistingOtp('');
    setExistingError('');
    setExistingHint(false);
    clearOtpSession();
  };

  const handleExistingSendOTP = async (isRetry = false) => {
    const mobile = existingMobile.replace(/\D/g, '');
    if (mobile.length !== 10) {
      setExistingError('Please enter a valid 10-digit mobile number');
      return;
    }
    setExistingLoading(true);
    setExistingError('');
    try {
      if (isMsg91Enabled()) {
        // Pre-flight: check the user is registered before burning an SMS credit.
        // (Skip pre-flight on retry — we already validated on the first send.)
        if (!isRetry) {
          await axios.post(`${API}/auth/mobile-login`, { mobile_number: mobile });
        }
        await loadMsg91Widget();
        // Prefer MSG91's retryOtp for resends — it respects the OTP TTL and
        // avoids consuming a new SMS credit unnecessarily. Falls back to a
        // fresh sendOtp if retryOtp is unavailable in the widget.
        if (isRetry) {
          try { await msg91RetryOtp(); } catch (_e) { await msg91SendOtp(mobile); }
        } else {
          await msg91SendOtp(mobile);
        }
        setExistingStep('otp');
        setExistingHint(false); // real OTP — no demo hint
        saveOtpSession({ flow: 'existing', mobile });
      } else {
        const res = await axios.post(`${API}/auth/mobile-login`, { mobile_number: mobile });
        if (res.data.success) {
          setExistingStep('otp');
          setExistingHint(true);
          saveOtpSession({ flow: 'existing', mobile });
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err?.message || 'Failed to send OTP';
      setExistingError(detail);
    } finally {
      setExistingLoading(false);
    }
  };

  const handleExistingVerify = async () => {
    if (existingOtp.length !== 6) {
      setExistingError('Please enter the 6-digit OTP');
      return;
    }
    setExistingLoading(true);
    setExistingError('');
    try {
      let res;
      if (isMsg91Enabled()) {
        const accessToken = await msg91VerifyOtp(existingOtp);
        res = await axios.post(`${API}/auth/mobile-login/verify-msg91`, {
          access_token: accessToken,
          mobile_number: existingMobile.replace(/\D/g, ''),
        });
      } else {
        res = await axios.post(`${API}/auth/mobile-verify`, {
          mobile_number: existingMobile.replace(/\D/g, ''),
          otp: existingOtp
        });
      }
      if (res.data.success) {
        clearOtpSession();
        localStorage.setItem('user_id', res.data.user_id);
        localStorage.setItem('user_name', res.data.name);
        localStorage.setItem('user_email', res.data.email || '');
        if (res.data.session_token) localStorage.setItem('user_session_token', res.data.session_token);
        navigate(postAuthDestination());
      }
    } catch (err) {
      setExistingError(err.response?.data?.detail || err?.message || 'Invalid OTP');
    } finally {
      setExistingLoading(false);
    }
  };

  // ============== NEW USER REGISTRATION (Mobile + OTP + Details) ==============
  const resetRegister = () => {
    setShowRegisterModal(false);
    setRegStep('mobile');
    setRegMobile('');
    setRegOtp('');
    setFullName('');
    setEmail('');
    setEmailTouched(false);
    setResidentialAddress('');
    setNotificationConsent(false);
    setPendingUserId('');
    setRegError('');
    setRegHint(false);
    setPostAuthIntent('dashboard');
    clearOtpSession();
  };

  const handleRegSendOTP = async (isRetry = false) => {
    const cleaned = regMobile.replace(/\D/g, '').slice(-10);
    if (!/^\d{10}$/.test(cleaned)) {
      setRegError('Please enter a valid 10-digit mobile number');
      return;
    }
    setRegLoading(true);
    setRegError('');
    try {
      if (isMsg91Enabled()) {
        await loadMsg91Widget();
        if (isRetry) {
          try { await msg91RetryOtp(); } catch (_e) { await msg91SendOtp(cleaned); }
        } else {
          await msg91SendOtp(cleaned);
        }
        setRegMobile(cleaned);
        setRegStep('otp');
        setRegHint(false);
        saveOtpSession({ flow: 'register', mobile: cleaned });
      } else {
        const res = await axios.post(`${API}/auth/mobile-register/send-otp`, {
          mobile_number: cleaned
        });
        if (res.data.success) {
          setRegMobile(cleaned);
          setRegStep('otp');
          setRegHint(true);
          saveOtpSession({ flow: 'register', mobile: cleaned });
        }
      }
    } catch (err) {
      setRegError(err.response?.data?.detail || err?.message || 'Failed to send OTP');
    } finally {
      setRegLoading(false);
    }
  };

  const handleRegVerifyOTP = async () => {
    if (regOtp.length !== 6) {
      setRegError('Please enter the 6-digit OTP');
      return;
    }
    setRegLoading(true);
    setRegError('');
    try {
      let res;
      if (isMsg91Enabled()) {
        const accessToken = await msg91VerifyOtp(regOtp);
        res = await axios.post(`${API}/auth/mobile-register/verify-msg91`, {
          access_token: accessToken,
          mobile_number: regMobile,
        });
      } else {
        res = await axios.post(`${API}/auth/mobile-register/verify-otp`, {
          mobile_number: regMobile,
          otp: regOtp
        });
      }
      if (res.data.success) {
        clearOtpSession();
        setPendingUserId(res.data.user_id);
        if (res.data.is_new_user) {
          // Go to complete-profile step — persist the interim token so the
          // /auth/register call in step 2 is already authenticated.
          if (res.data.session_token) localStorage.setItem('user_session_token', res.data.session_token);
          setRegStep('details');
        } else {
          // Already registered with this mobile — sign them in directly
          localStorage.setItem('user_id', res.data.user_id);
          localStorage.setItem('user_name', res.data.name || '');
          localStorage.setItem('user_email', res.data.email || '');
          if (res.data.session_token) localStorage.setItem('user_session_token', res.data.session_token);
          navigate(postAuthDestination());
        }
      }
    } catch (err) {
      setRegError(err.response?.data?.detail || err?.message || 'Invalid OTP');
    } finally {
      setRegLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!fullName.trim()) return setRegError('Please enter your full name');
    if (!isValidEmail(email)) return setRegError('Please enter a valid email (e.g. name@example.com)');
    if (!residentialAddress.trim()) return setRegError('Please enter your residential address');
    if (!notificationConsent) return setRegError('Please tick the notification consent box to continue');

    setRegLoading(true);
    setRegError('');
    try {
      const res = await axios.post(`${API}/auth/register`, {
        user_id: pendingUserId,
        full_name: fullName,
        mobile_number: regMobile,
        email,
        residential_address: residentialAddress,
        notification_consent: notificationConsent
      });
      if (res.data.success) {
        localStorage.setItem('user_id', res.data.user_id);
        localStorage.setItem('user_name', res.data.name);
        localStorage.setItem('user_email', res.data.email);
        if (res.data.session_token) localStorage.setItem('user_session_token', res.data.session_token);
        navigate(postAuthDestination());
      }
    } catch (err) {
      setRegError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegLoading(false);
    }
  };

  // ============== SIGN-IN DROPDOWN ROUTING ==============
  const handleSignInOption = (role) => {
    setSignInOpen(false);
    if (role === 'existing') {
      setShowExistingModal(true);
    } else if (role === 'partner') {
      navigate('/partner');
    } else if (role === 'agent') {
      navigate('/agent');
    } else if (role === 'admin') {
      navigate('/admin');
    }
  };

  return (
    <>
    <div
      className="theme-sunset"
      data-testid="landing-page"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >

      {/* ============== TOP BAR ============== */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#E8DCC9]/85 border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="home-brand">
            <BrandMark size={36} textClass="text-base sm:text-lg text-[#1A1A1A]" />
          </Link>
          <nav className="hidden sm:flex items-center gap-7 text-xs uppercase tracking-[0.18em] text-[#525252]">
            <Link to="/" data-testid="nav-home" className="hover:text-[#FF5A1F] transition-colors">Home</Link>
            <Link to="/pricing" data-testid="nav-pricing" className="hover:text-[#FF5A1F] transition-colors">Pricing</Link>
            <Link to="/faq" data-testid="nav-faq" className="hover:text-[#FF5A1F] transition-colors">FAQ</Link>
            <Link to="/track" data-testid="nav-track" className="hover:text-[#FF5A1F] transition-colors">Track</Link>
            <button
              data-testid="nav-signin-btn"
              onClick={() => setShowExistingModal(true)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-white hover:bg-[#FF5A1F] transition-colors text-xs font-bold tracking-wide"
            >
              <User size={13} /> Sign In
            </button>
          </nav>
          {/* mobile signin */}
          <div className="sm:hidden">
            <button
              data-testid="nav-signin-btn-mobile"
              onClick={() => setShowExistingModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#1A1A1A] text-white text-xs font-bold"
            >
              <User size={14} /> Sign In
            </button>
          </div>
        </div>
      </header>

      {/* ============== HERO ============== */}
      <section className="relative">
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-7 pb-8 z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
            <div className="lg:col-span-7 xl:col-span-7 max-w-4xl">
            {/* Eyebrow pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 backdrop-blur border border-[#1A1A1A]/12 mb-8 shadow-[2px_2px_0px_0px_#FF5A1F]">
              <span className="text-[#FF5A1F]">──</span>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] font-bold text-[#1A1A1A]">
                LuggageFreeJourney — Doorstep pickup & delivery
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-8xl font-bold tracking-tighter leading-[0.92] text-[#1A1A1A]">
              Drop the bag.<br />
              <span className="text-[#FF5A1F]">Travel free.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#3F3F3F] max-w-xl">
              <span className="font-black tracking-tight text-[#1A1A1A]">LuggageFreeJourney</span> handles the bags —
              doorstep pickup, live GPS, OTP-verified delivery between cities. Flat
              <span className="text-[#1A1A1A] font-semibold"> ₹500 + ₹65/kg</span>, GST included.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-book-pickup-btn"
                onClick={handleBookPickup}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-bold uppercase tracking-widest bg-[#FF5A1F] text-white hover:bg-[#E64200] hover:scale-[1.02] transition-all shadow-[0_8px_24px_rgba(255,90,31,0.3)]"
              >
                Book a pickup <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-register-btn"
                onClick={() => { setPostAuthIntent('dashboard'); setShowRegisterModal(true); }}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-bold uppercase tracking-widest bg-white/85 backdrop-blur text-[#1A1A1A] border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition"
              >
                New user? Register
              </button>
              <InstallAppButton className="text-[#1A1A1A] hover:text-[#FF5A1F]" label="INSTALL APP" manifest="/manifest.json" />
            </div>

            {/* Quick track */}
            <div className="mt-4 max-w-xl rounded-2xl bg-white/92 backdrop-blur border border-[#1A1A1A]/10 p-5 shadow-sm">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#737373] mb-2">Track your bag</div>
              <div className="flex items-center gap-2">
                <Search size={18} className="flex-shrink-0 text-[#525252]" />
                <input
                  data-testid="quick-track-input"
                  type="text"
                  placeholder="ENTER BAGGAGE TAG"
                  className="flex-1 bg-transparent border-b border-[#1A1A1A]/15 outline-none px-1 py-1 text-sm font-semibold tracking-wider uppercase placeholder:text-[#1A1A1A]/30 text-[#1A1A1A]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      navigate(`/track?tag=${encodeURIComponent(e.target.value.trim())}`);
                    }
                  }}
                />
                <button
                  data-testid="quick-track-go"
                  className="bg-[#FF5A1F] text-white px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider hover:bg-[#E64200] transition-all"
                  onClick={() => {
                    const v = document.querySelector('[data-testid="quick-track-input"]')?.value?.trim();
                    if (v) navigate(`/track?tag=${encodeURIComponent(v)}`);
                  }}
                >Go</button>
              </div>
            </div>

            {/* Stats cards */}
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trust-strip">
              {[
                { value: '₹10,000', label: 'Insurance coverage', c: '#FF5A1F' },
                { value: 'Live GPS', label: 'Real-time tracking', c: '#2E7D32' },
                { value: 'OTP', label: 'Verified delivery', c: '#1A1A1A' },
                { value: `${citiesServed.length || 8}+`, label: 'Indian cities served', c: '#0B5394' },
              ].map((m, i) => (
                <div key={i} className="rounded-2xl bg-white/92 backdrop-blur border border-[#1A1A1A]/10 p-5 hover:shadow-lg transition">
                  <div className="w-2 h-7 rounded-full mb-3" style={{ background: m.c }} />
                  <div className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">{m.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#737373] mt-2">{m.label}</div>
                </div>
              ))}
            </div>
            </div>

            {/* Right column — auto-scrolling hero carousel (branded) */}
            <div className="lg:col-span-5 xl:col-span-5 mt-6 lg:mt-2 lg:sticky lg:top-24 self-start w-full">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="bg-white/80 backdrop-blur border-y border-[#1A1A1A]/10" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#FF5A1F] mb-4 font-semibold">── How it works</div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter leading-[0.95] max-w-3xl text-[#1A1A1A]">
            Five steps. <span className="text-[#737373]">Zero stress.</span>
          </h2>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { n: '01', t: 'Book online', d: 'Add bags, addresses, pickup date. Pay via UPI / card.', Icon: Smartphone, c: '#FF5A1F' },
              { n: '02', t: 'We pick up', d: 'Our agent arrives at your door with QR-coded tags.', Icon: Car, c: '#0B5394' },
              { n: '03', t: 'In transit', d: 'Bags move under live GPS. You watch in real-time.', Icon: Truck, c: '#2E7D32' },
              { n: '04', t: 'Out for delivery', d: 'Your destination contact gets a heads-up call.', Icon: BellRing, c: '#B45309' },
              { n: '05', t: 'OTP delivered', d: 'Bag handed over only after the OTP you share.', Icon: KeyRound, c: '#1A1A1A' },
            ].map((s, i) => (
              <div key={i} className="group rounded-2xl bg-[#FAF6EE] border border-[#1A1A1A]/10 p-5 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs text-[#737373] font-mono tracking-wider">{s.n}</div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.c }}>
                    <s.Icon size={17} className="text-white" />
                  </div>
                </div>
                <div className="text-sm font-bold text-[#1A1A1A]">{s.t}</div>
                <div className="text-xs text-[#525252] mt-1.5 leading-snug">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== PRICE STRIP ============== */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3">
          <div className="rounded-full bg-[#1A1A1A]">
            <div className="rounded-full px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-white">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF5A1F] font-bold">Pricing</span>
                <span className="text-sm md:text-base font-bold tracking-tight">₹500 base · ₹65/kg · 18% GST</span>
                <span className="hidden md:inline text-xs text-white/65">No tiers. No surge. No hidden fees.</span>
              </div>
              <Link
                to="/pricing"
                data-testid="see-full-pricing"
                className="inline-flex items-center gap-1.5 bg-[#FF5A1F] text-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-[#E64200] transition-colors"
              >
                See full pricing <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============== CITIES SERVED ============== */}
      {citiesServed.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-6" data-testid="cities-served">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#FF5A1F] mb-4 font-semibold">── Coverage</div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter leading-[0.95] max-w-3xl text-[#1A1A1A]">
            Currently serving across these cities.
          </h2>
          <p className="mt-4 text-sm text-[#525252] max-w-xl">
            Door-to-door pickup and delivery available in {citiesServed.length} Indian cities. More cities added every month.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="cities-pills">
            {citiesServed.map((c, i) => (
              <div
                key={i}
                data-testid={`city-pill-${c.toLowerCase()}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1A1A1A]/12 bg-white/92 backdrop-blur text-sm font-semibold text-[#1A1A1A] hover:bg-[#FF5A1F] hover:text-white hover:border-transparent transition-all"
              >
                <MapPin size={14} className="text-[#FF5A1F]" />
                {c}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============== FAQ TEASER ============== */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-4 grid grid-cols-1 md:grid-cols-12 gap-8" data-testid="faq-teaser">
        <div className="md:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#FF5A1F] mb-3 font-semibold">── FAQ</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tighter leading-[0.95] text-[#1A1A1A]">
            Common questions, quick answers.
          </h2>
          <Link to="/faq" data-testid="see-all-faq" className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-widest font-semibold text-[#1A1A1A] border-b border-[#1A1A1A]/30 pb-1 hover:text-[#FF5A1F] hover:border-[#FF5A1F]">
            See all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="md:col-span-7 space-y-2.5">
          {[
            { q: 'Is my luggage insured?', a: 'Yes — every booking is covered up to ₹10,000.' },
            { q: 'How fast is delivery?', a: '24–48 hours inter-city, same-day intra-city.' },
            { q: 'Can I edit a paid booking?', a: 'Yes — up until pickup begins. Top-up or refund is automatic.' },
          ].map((f, i) => (
            <details key={i} className="group rounded-xl border border-[#1A1A1A]/12 bg-white/92 backdrop-blur px-4 py-3" data-testid={`landing-faq-${i}`}>
              <summary className="cursor-pointer text-sm font-semibold tracking-tight list-none flex items-center justify-between text-[#1A1A1A]">
                {f.q}
                <span className="transition-transform group-open:rotate-45 text-[#FF5A1F] text-xl leading-none">+</span>
              </summary>
              <p className="mt-2 text-xs text-[#525252] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ============== CTA STRIP ============== */}
      <section className="relative bg-white/80 backdrop-blur border-t border-[#1A1A1A]/10 py-7" data-testid="final-cta">
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center z-10">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-[#FF5A1F] font-bold mb-3">
            ── LuggageFreeJourney
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95] text-[#1A1A1A]">
            Drop the bag.<br />
            <span className="text-[#FF5A1F]">Travel free.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#525252] max-w-xl mx-auto">
            From ₹500. Booked in 60 seconds. Delivered to the right door.
          </p>
          <button
            data-testid="footer-cta-book"
            onClick={handleBookPickup}
            className="mt-10 inline-flex items-center gap-2 bg-[#FF5A1F] text-white px-9 py-4 rounded-full text-sm font-bold uppercase tracking-widest hover:bg-[#E64200] hover:scale-[1.02] transition-all shadow-[0_8px_24px_rgba(255,90,31,0.3)]"
          >
            Book a pickup <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <Footer />
    </div>

      {/* ============== EXISTING USER MODAL (Mobile + OTP) ============== */}
      {showExistingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="relative rounded-2xl bg-white border border-[#1A1A1A]/10 shadow-2xl p-8 max-w-md w-full my-8 text-[#1A1A1A]">
            <button
              data-testid="existing-modal-close-x"
              onClick={resetExisting}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#FAF6EE] hover:bg-[#FF5A1F] hover:text-white flex items-center justify-center text-[#1A1A1A] transition-all border border-[#1A1A1A]/10"
            >
              <X size={18} />
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#FF5A1F] flex items-center justify-center">
                <User size={26} className="text-white" strokeWidth={2} />
              </div>
              <h2 className="text-2xl font-bold tracking-tight mb-2">
                Existing user login
              </h2>
              <p className="text-xs text-white/55 uppercase tracking-[0.25em]" style={{color:'#737373'}}>
                {existingStep === 'mobile' && 'Step 1 of 2 — Enter mobile'}
                {existingStep === 'otp' && 'Step 2 of 2 — Verify OTP'}
              </p>
            </div>

            {existingStep === 'mobile' && (
              <div data-testid="existing-mobile-section">
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-2">
                  Mobile number (10 digits)
                </label>
                <input
                  data-testid="existing-mobile-input"
                  type="text"
                  inputMode="numeric"
                  value={existingMobile}
                  onChange={(e) => setExistingMobile(e.target.value.replace(/\D/g, ''))}
                  placeholder="Your registered mobile"
                  className="w-full mb-2 px-4 py-3 rounded-xl bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 tracking-widest outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
                  maxLength={10}
                />
                <p className="text-xs text-white/45 mb-4">
                  OTP will be sent to your registered mobile
                </p>
                {existingError && <p data-testid="existing-error" className="text-[#FF6B6B] text-sm mb-4">{existingError}</p>}
                <button
                  data-testid="existing-send-otp-btn"
                  onClick={handleExistingSendOTP}
                  disabled={existingLoading}
                  className="w-full py-3 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] hover:shadow-[0_8px_24px_rgba(255,90,31,0.4)] disabled:opacity-50 transition-all"
                >
                  {existingLoading ? 'SENDING...' : 'SEND OTP'}
                </button>
              </div>
            )}

            {existingStep === 'otp' && (
              <div data-testid="existing-otp-section">
                <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/15">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Registered mobile</div>
                  <div className="text-sm text-white/85">
                    OTP sent to <strong className="text-white">{existingMobile}</strong>
                  </div>
                </div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-2">Enter OTP</label>
                <input
                  data-testid="existing-otp-input"
                  type="text"
                  inputMode="numeric"
                  value={existingOtp}
                  onChange={(e) => setExistingOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="6-digit OTP"
                  className="w-full mb-2 px-4 py-3 rounded-xl bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 tracking-widest text-center text-lg outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
                  maxLength={6}
                />
                {existingHint && (
                  <p className="text-xs text-white/55 mb-4">Demo OTP: 123456</p>
                )}
                {existingError && <p data-testid="existing-error" className="text-[#FF6B6B] text-sm mb-4">{existingError}</p>}
                <button
                  data-testid="existing-verify-otp-btn"
                  onClick={handleExistingVerify}
                  disabled={existingLoading}
                  className="w-full py-3 mb-3 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] hover:shadow-[0_8px_24px_rgba(255,90,31,0.4)] disabled:opacity-50 transition-all"
                >
                  {existingLoading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
                </button>
                <OtpResendButton
                  testId="existing-resend-otp-btn"
                  onResend={() => handleExistingSendOTP(true)}
                  variant="light"
                />
              </div>
            )}

            <button
              data-testid="close-existing-modal-btn"
              onClick={resetExisting}
              className="mt-4 text-center w-full text-sm text-[#525252] underline hover:text-[#FF5A1F] transition"
            >
              CLOSE
            </button>
          </div>
        </div>
      )}

      {/* ============== REGISTRATION MODAL (Mobile + OTP + Details) ============== */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="relative rounded-2xl bg-white border border-[#1A1A1A]/10 shadow-2xl p-8 max-w-md w-full my-8 text-[#1A1A1A]">
            <button
              data-testid="register-modal-close-x"
              onClick={resetRegister}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#FAF6EE] hover:bg-[#FF5A1F] hover:text-white flex items-center justify-center text-[#1A1A1A] transition-all border border-[#1A1A1A]/10"
            >
              <X size={18} />
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#FF5A1F] flex items-center justify-center">
                <Users size={26} className="text-white" strokeWidth={2} />
              </div>
              <h2 className="text-2xl font-bold tracking-tight mb-2">
                {regStep === 'details' ? 'Complete profile' : (postAuthIntent === 'book' ? 'Continue to book' : 'Register')}
              </h2>
              <p className="text-xs text-white/55 uppercase tracking-[0.25em]" style={{color:'#737373'}}>
                {regStep === 'mobile' && (postAuthIntent === 'book' ? 'Step 1 — Enter mobile' : 'Step 1 of 3 — Enter mobile')}
                {regStep === 'otp' && (postAuthIntent === 'book' ? 'Step 2 — Verify OTP' : 'Step 2 of 3 — Verify OTP')}
                {regStep === 'details' && 'Step 3 of 3 — Your details'}
              </p>
            </div>

            {regStep === 'mobile' && (
              <div data-testid="reg-mobile-section">
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-2">
                  Mobile number (10 digits)
                </label>
                <input
                  data-testid="reg-mobile-input-step1"
                  type="tel"
                  inputMode="numeric"
                  value={regMobile}
                  onChange={(e) => setRegMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="98XXXXXXXX"
                  className="w-full mb-2 px-4 py-3 rounded-xl bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 tracking-widest outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
                  maxLength={10}
                />
                <p className="text-xs text-white/45 mb-4">
                  We&apos;ll send a 6-digit OTP to verify your number.
                </p>
                {regError && <p data-testid="reg-error" className="text-[#FF6B6B] text-sm mb-4">{regError}</p>}
                <button
                  data-testid="reg-send-otp-btn"
                  onClick={handleRegSendOTP}
                  disabled={regLoading}
                  className="w-full py-3 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] hover:shadow-[0_8px_24px_rgba(255,90,31,0.4)] disabled:opacity-50 transition-all"
                >
                  {regLoading ? 'SENDING...' : 'SEND OTP'}
                </button>
              </div>
            )}

            {regStep === 'otp' && (
              <div data-testid="reg-otp-section">
                <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/15">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Mobile</div>
                  <div className="text-sm text-white/85">
                    OTP sent to <strong className="text-white">+91 {regMobile}</strong>
                  </div>
                </div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-2">Enter OTP</label>
                <input
                  data-testid="reg-otp-input"
                  type="text"
                  inputMode="numeric"
                  value={regOtp}
                  onChange={(e) => setRegOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="6-digit OTP"
                  className="w-full mb-2 px-4 py-3 rounded-xl bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 tracking-widest text-center text-lg outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
                  maxLength={6}
                />
                {regHint && (
                  <p className="text-xs text-white/55 mb-4">Demo OTP: 123456</p>
                )}
                {regError && <p data-testid="reg-error" className="text-[#FF6B6B] text-sm mb-4">{regError}</p>}
                <button
                  data-testid="reg-verify-otp-btn"
                  onClick={handleRegVerifyOTP}
                  disabled={regLoading}
                  className="w-full py-3 mb-3 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] hover:shadow-[0_8px_24px_rgba(255,90,31,0.4)] disabled:opacity-50 transition-all"
                >
                  {regLoading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
                </button>
                <OtpResendButton
                  testId="reg-resend-otp-btn"
                  onResend={() => handleRegSendOTP(true)}
                  variant="light"
                />
              </div>
            )}

            {regStep === 'details' && (
              <div data-testid="reg-details-section" className="space-y-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Mobile number (verified)</label>
                  <input
                    data-testid="reg-mobile-input"
                    type="text"
                    value={regMobile}
                    readOnly
                    className="w-full px-4 py-3 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/10 text-[#525252] tracking-widest outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Full name</label>
                  <input
                    data-testid="reg-name-input"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Email address</label>
                  <input
                    data-testid="reg-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className={`w-full px-4 py-3 rounded-xl bg-white border text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 outline-none focus:bg-white focus:ring-2 transition ${
                      emailTouched && email && !isValidEmail(email)
                        ? 'border-[#FF6B6B] focus:border-[#FF6B6B] focus:ring-[#FF6B6B]/15'
                        : 'border-[#1A1A1A]/15 focus:border-[#FF5A1F] focus:ring-[#FF5A1F]/15'
                    }`}
                  />
                  {emailTouched && email && !isValidEmail(email) && (
                    <p data-testid="reg-email-error" className="mt-1 text-xs text-[#FF6B6B]">
                      Please enter a valid email (e.g. name@example.com)
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Residential address</label>
                  <textarea
                    data-testid="reg-address-input"
                    value={residentialAddress}
                    onChange={(e) => setResidentialAddress(e.target.value)}
                    placeholder="Full residential address"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#1A1A1A]/15 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition resize-none"
                  />
                </div>
                <label
                  className="flex items-start gap-3 p-3 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 cursor-pointer hover:border-[#FF5A1F]/40 transition"
                  data-testid="reg-consent-label"
                >
                  <input
                    data-testid="reg-consent-checkbox"
                    type="checkbox"
                    checked={notificationConsent}
                    onChange={(e) => setNotificationConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#FF5A1F] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs leading-snug text-[#1A1A1A]">
                    I consent <strong>Luggage Free Journey</strong> to send notifications via SMS, WhatsApp about the status of my bookings. <span className="text-[#FF5A1F]">*</span>
                  </span>
                </label>
                {regError && <p data-testid="reg-error" className="text-[#FF6B6B] text-sm">{regError}</p>}
                <button
                  data-testid="reg-complete-btn"
                  onClick={handleRegister}
                  disabled={regLoading || !notificationConsent || !isValidEmail(email) || !fullName.trim() || !residentialAddress.trim()}
                  className="w-full py-3 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] hover:shadow-[0_8px_24px_rgba(255,90,31,0.4)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {regLoading ? 'REGISTERING...' : 'COMPLETE REGISTRATION'}
                </button>
              </div>
            )}

            <button
              data-testid="close-register-modal-btn"
              onClick={resetRegister}
              className="mt-4 text-center w-full text-sm text-[#525252] underline hover:text-[#FF5A1F] transition"
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Landing;
